import React, { UIEvent, useCallback, useEffect, useRef, useState } from "react";
import {
    CustomLandingItem,
    getCustomLandingPagesGlobal,
} from "../utils/custom-landing";
import { getImageUrl, isSuperShop } from "../utils";
import { MdEdit as MdEditIcon } from "react-icons/md";
import { Loading } from "mdi-material-ui";
import { useSelector } from "react-redux";
import { RootState } from "../app/store";

const MdEdit = MdEditIcon as React.ElementType;

const LandingThemeSelect = ({
    onThemeSelect,
    onEditTheme,
    onSkip,
}: {
    onThemeSelect: (theme: CustomLandingItem) => void;
    onEditTheme?: (theme: CustomLandingItem) => void;
    onSkip?: () => void;
}) => {
    const shop = useSelector((state: RootState) => state.auth.shop);

    const [themes, setThemes] = useState<CustomLandingItem[]>([]);
    const [loading, setLoading] = useState<boolean>(false);

    const apiCalling = useRef(false);

    const [pagePagination, setPagePagination] = useState({
        total: 0,
        lastPage: 2,
        currentPage: 1,
    });

    const getThemeCallback = useCallback(
        (currentPage: number): Promise<CustomLandingItem[]> => {
            return new Promise((resolve, reject) => {
                setLoading(true);
                apiCalling.current = true;

                getCustomLandingPagesGlobal({
                    currentPage: currentPage,
                    perPage: 12,
                })
                    .then((res) => {
                        setPagePagination((prev) => ({
                            ...prev,
                            total: res.page.totalIndex,
                            lastPage: res.page.totalPage,
                            currentPage: res.page.currentPage,
                        }));
                        resolve(res.data);
                    })
                    .catch((err) => {
                        reject(err);
                    })
                    .finally(() => {
                        setLoading(false);
                        apiCalling.current = false;
                    });
            });
        },
        []
    );

    useEffect(() => {
        getThemeCallback(1).then(setThemes);
    }, [getThemeCallback]);

    const handlePageScroll = (e: UIEvent<HTMLDivElement>) => {
        const target = e.target as HTMLDivElement;

        if (
            !apiCalling.current &&
            pagePagination.currentPage < pagePagination.lastPage &&
            target.scrollTop + target.clientHeight + 120 >= target.scrollHeight
        ) {
            getThemeCallback(pagePagination.currentPage + 1).then(
                (newThemes) => {
                    setThemes((prev) => [...prev, ...newThemes]);
                }
            );

            const updatedPagination = {
                ...pagePagination,
                currentPage: pagePagination.currentPage + 1,
            };

            setPagePagination(updatedPagination);
        }
    };

    return (
        <div className="p-4 rounded-md bg-white">
            <div className="flex items-center justify-between gap-4 border-b border-gray-300 pb-2 mb-2">
                <p className="text-base font-semibold">
                    Select a theme for your landing page
                </p>

                {/* skip button */}
                {onSkip && (
                    <button
                        type="button"
                        className="text-white p-2 px-4 rounded-md bg-[#22C55E] hover:bg-[#22C55E]/80"
                        onClick={onSkip}
                    >
                        Skip
                    </button>
                )}
            </div>
            <div
                onScroll={handlePageScroll}
                className="grid grid-cols-2 xl:grid-cols-3 gap-4 w-[80vw] max-w-[1400px] max-h-[80vh] overflow-y-auto"
            >
                {!!themes?.length
                    ? themes.map((theme, index) => (
                          <div key={index} className="border p-2">
                              <div className="flex gap-2 justify-between">
                                  <h1 className="text-lg font-semibold mb-2">
                                      {theme.name}
                                  </h1>

                                  {/* edit button */}
                                  {onEditTheme && isSuperShop(shop) && (
                                      <button
                                          type="button"
                                          className="text-gray-800 hover:text-gray-900 p-2 px-4 rounded-md bg-gray-200 hover:bg-gray-300"
                                          onClick={() => onEditTheme(theme)}
                                      >
                                          <MdEdit className="text-lg" />
                                      </button>
                                  )}
                              </div>
                              <div
                                  className="w-full h-64 p-2 cursor-pointer"
                                  onClick={() => onThemeSelect(theme)}
                              >
                                  <img
                                      src={
                                          theme.images
                                              ? getImageUrl(theme.images || "")
                                              : ""
                                      }
                                      alt={theme.name}
                                      className="w-full h-full object-contain"
                                  />
                              </div>
                          </div>
                      ))
                    : "No themes found"}
            </div>
            {loading && (
                <div className="w-full p-2 text-center text-4xl">
                    <Loading fontSize="inherit" className="animate-spin" />
                </div>
            )}
        </div>
    );
};

export default LandingThemeSelect;
